import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const errText = (err, fb) => err?.response?.data?.detail || fb;

const AvatarUpload = ({ user, onUploaded, setMsg }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/users/${user.id}/avatar`, fd);
      onUploaded(data.avatar);
      setMsg({ ok: true, text: "✅ Foto de perfil atualizada!" });
    } catch (err) { setMsg({ ok: false, text: `❌ ${errText(err, "Erro ao enviar foto")}` }); }
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <div className="flex items-center gap-4">
      <img src={user.avatar} alt="" className="w-20 h-20 rounded-full bg-arc object-cover border-4 border-white shadow" data-testid="edit-user-avatar" />
      <div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="edit-user-avatar-input" />
        <button type="button" disabled={busy} onClick={() => ref.current.click()} className="arc-btn arc-btn-outline text-sm py-2 px-4" data-testid="edit-user-avatar-btn">
          {busy ? "Enviando..." : "📷 Trocar foto"}
        </button>
        <div className="text-xs text-arc-muted mt-1">JPG, PNG, WEBP ou GIF · até 5MB</div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold text-arc-muted uppercase">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export const AdminUserEditor = ({ user, classes, open, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [current, setCurrent] = useState(user);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    if (!user) return;
    setCurrent(user); setMsg(null);
    setForm({ name: user.name, email: user.email, ra: user.ra || "", class_id: user.class_id || "", new_password: "" });
  }, [user]);
  if (!user || !current) return null;
  const isStudent = user.role === "student";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e, resetToRa = false) => {
    e?.preventDefault();
    const body = { name: form.name, email: form.email };
    if (isStudent) Object.assign(body, { ra: form.ra, class_id: form.class_id || null, reset_password_to_ra: resetToRa });
    else if (form.new_password) body.new_password = form.new_password;
    try {
      const { data } = await axios.put(`${API}/admin/users/${user.id}`, body);
      setCurrent(data); setForm({ ...form, new_password: "" });
      setMsg({ ok: true, text: resetToRa ? `✅ Senha redefinida para o RA (${data.ra}).` : "✅ Dados salvos!" });
      onSaved();
    } catch (err) { setMsg({ ok: false, text: `❌ ${errText(err, "Erro ao salvar")}` }); }
  };
  const uploaded = (avatar) => { setCurrent({ ...current, avatar }); onSaved(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-fredoka text-2xl">Editar usuário</DialogTitle>
          <DialogDescription>Perfil: {isStudent ? "Aluno" : user.role === "teacher" ? "Professor" : "Admin"}</DialogDescription>
        </DialogHeader>
        {msg && <div data-testid="edit-user-msg" className={`p-3 rounded-2xl text-sm font-semibold ${msg.ok ? "bg-arc-success text-arc-text" : "bg-pink-100 text-arc-accent"}`}>{msg.text}</div>}
        <AvatarUpload user={current} onUploaded={uploaded} setMsg={setMsg} />
        <form onSubmit={save} className="space-y-4 mt-2">
          <Field label="Nome completo"><input required className="arc-input" value={form.name} onChange={set("name")} data-testid="edit-user-name" /></Field>
          <Field label="E-mail ou login"><input required className="arc-input" value={form.email} onChange={set("email")} data-testid="edit-user-email" /></Field>
          {isStudent ? (
            <>
              <Field label="RA (também é a senha do aluno)"><input className="arc-input font-mono" value={form.ra} onChange={set("ra")} data-testid="edit-user-ra" /></Field>
              <Field label="Turma">
                <select className="arc-input" value={form.class_id} onChange={set("class_id")} data-testid="edit-user-class">
                  <option value="">Sem turma</option>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <div className="p-3 rounded-2xl bg-arc text-xs text-arc-muted">🔐 A senha do aluno é sempre o RA. Ao alterar o RA, a senha muda junto.</div>
            </>
          ) : (
            <Field label="Nova senha (opcional)"><input type="text" minLength={4} className="arc-input" value={form.new_password} onChange={set("new_password")} placeholder="Deixe em branco para manter" data-testid="edit-user-password" /></Field>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <button type="submit" className="arc-btn arc-btn-primary flex-1" data-testid="save-user-btn">Salvar alterações</button>
            {isStudent && <button type="button" onClick={(e) => save(e, true)} className="arc-btn arc-btn-outline flex-1" data-testid="reset-password-ra-btn">Redefinir senha p/ RA</button>}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
